/**
 * Thin adapters over legacy `requesting` / generalRequest for redesign portal screens.
 * Prefer these helpers over hard-coded demo copy.
 */
import store from '@/store'
import moment from 'moment-jalaali'
// Configure Jalali month names without changing the app's current language.
if (typeof moment.loadPersian === 'function') {
  const previousLocale = moment.locale()
  moment.loadPersian({dialect: 'persian-modern', usePersianDigits: false})
  moment.locale(previousLocale)
}
import {
  profileDisplayName,
  profileFirstName,
  profileInitials,
} from './portalProfile'

export {
  profileDisplayName,
  profileFirstName,
  profileInitials,
}

const PERSIAN_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹']

export function portalRequest(type, name, paramsQuery = {}, body = {}) {
  return store.dispatch('generalRequest', {
    type,
    name,
    paramsQuery,
    body,
    headers: {},
  })
}

/** generalRequest returns the JSON body, already unwrapped from Axios. */
export function portalPayload(resp) {
  return (resp && resp.data) || {}
}

// Care mutations must have a server success receipt, including legacy HTTP-200 failures.
export function careReceipt(resp) {
  const successful = resp && (resp.ok === true || String(resp.type || '').toUpperCase() === 'SUCCESS')
  if (!successful || !resp.data || typeof resp.data !== 'object') {
    const error = new Error((resp && (resp.message || (resp.error && resp.error.code))) || 'CARE_REQUEST_FAILED')
    error.code = (resp && resp.data && (resp.data.reason_code || resp.data.code)) || (resp && resp.error && resp.error.code) || error.message
    throw error
  }
  return resp.data
}

export function getCachedProfile() {
  const fromStore = store.getters.getProfile
  if (fromStore && typeof fromStore === 'object') return fromStore
  try {
    const raw = localStorage.getItem('profile')
    if (!raw) return null
    const parsed = JSON.parse(raw)
    // CommitResolver sometimes persists `{key,data}` wrappers
    if (parsed && parsed.data && typeof parsed.data === 'object') return parsed.data
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch (e) {
    return null
  }
}

export function hasAuthToken() {
  return !!(localStorage.getItem('token') || store.state.token)
}

/** Fetch profile (commits SET_PROFILE) and return the store profile. Falls back to cache. */
export function fetchProfile() {
  const cached = getCachedProfile()
  if (!hasAuthToken()) {
    return Promise.resolve(cached)
  }
  return portalRequest('auth', 'getProfile')
      .then(() => getCachedProfile() || cached)
      .catch((err) => {
        if (cached) return cached
        throw err
      })
}

export function resolveDoctor(profile) {
  if (!profile) return null
  const assigned = profile.assignedDoctor
  const valid = doctor => doctor && typeof doctor === 'object' && !Array.isArray(doctor) && doctor.id != null
  if (Array.isArray(assigned)) return assigned.find(valid) || (valid(profile.doctor) ? profile.doctor : null)
  if (valid(assigned)) return assigned
  return valid(profile.doctor) ? profile.doctor : null
}

/** States that may appear as the client's next / upcoming session card. */
const UPCOMING_SESSION_STATES = {
  date_fixed: true,
  doctor_assigned: true,
}

/** Held, cancelled, or otherwise closed — never the "next session" hero. */
const TERMINAL_SESSION_STATES = {
  used: true,
  canceled: true,
  cancelled: true,
  done: true,
  completed: true,
}

export function sessionStateKey(session) {
  return String((session && (session.state || session.status)) || '')
    .trim()
    .toLowerCase()
}

/** True for date_fixed (scheduled, waiting to be held) or doctor_assigned. */
export function isUpcomingSessionState(session) {
  const state = sessionStateKey(session)
  return !!(state && UPCOMING_SESSION_STATES[state] && !TERMINAL_SESSION_STATES[state])
}

export function upcomingFromProfile(profile) {
  const list = profile && profile.four_future_sessions
  if (!Array.isArray(list)) return []
  return list.filter((s) => s && isUpcomingSessionState(s) && !isPastSession(s))
}

export function doctorDisplayName(doctor, lang = 'fa') {
  if (!doctor) return ''
  if (lang === 'fa') {
    return doctor.display_name || doctor.full_name || [doctor.en_name, doctor.en_family].filter(Boolean).join(' ')
  }
  const en = [doctor.en_name, doctor.en_family].filter(Boolean).join(' ')
  return en || doctor.display_name || doctor.full_name || ''
}

export function doctorInitials(doctor, lang = 'fa') {
  const name = doctorDisplayName(doctor, lang)
  if (!name) return '—'
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).slice(0, 2)
  return name.slice(0, 2)
}

/** Map the assigned therapist into the rematch card without inventing a rate. */
export function therapistUiFromDoctor(doctor, lang = 'fa') {
  if (!doctor) return null
  const rawPrice = doctor.session_price ?? doctor.price ?? (doctor.user && doctor.user.session_price)
  const price = rawPrice != null && String(rawPrice).trim() !== '' && Number.isFinite(Number(rawPrice))
    ? Number(rawPrice) : null
  return {
    id: doctor.id,
    name: doctorDisplayName(doctor, lang),
    initial: doctorInitials(doctor, lang),
    title: (lang !== 'fa' && doctor.en_subtitle) || doctor.subtitle || doctor.title || '',
    price,
    currency: doctor.currency || doctor.currency_code || 'toman',
  }
}

export function toFaDigits(value) {
  return String(value == null ? '' : value).replace(/\d/g, (d) => PERSIAN_DIGITS[d] || d)
}

function sessionMoment(session) {
  const raw = session && (session.fixed_date_time_g || session.session_date || session.from_date)
  return parseMomentSafe(raw)
}

function sessionInstant(session) {
  // Legacy display values are account-local wall clocks. New API responses
  // carry UTC instants so a different browser timezone cannot shift the clock.
  return parseMomentSafe(session && session.fixed_date_time_iso) || sessionMoment(session)
}

/**
 * Parse datetimes without moment.ISO_8601 in the format list.
 * moment-jalaali only accepts string format tokens; ISO_8601 throws
 * "config._f.match is not a function".
 *
 * DatesAndTime::getFromTimeAttribute emits `Y-m-d H:i` (no seconds) — that
 * format must be tried before bare `YYYY-MM-DD`, or the time becomes 00:00.
 */
function isUsableMoment(value) {
  return !!(value && typeof value.isValid === 'function' && value.isValid())
}

function parseMomentSafe(raw) {
  if (raw == null || raw === '') return null
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    try {
      const fromMs = moment(raw)
      return isUsableMoment(fromMs) ? fromMs : null
    } catch (e) {
      return null
    }
  }
  const text = String(raw).trim()
  // moment-jalaali cannot take a format *array* (returns undefined / throws).
  // ISO tokens with Z also throw on some versions — try one format at a time.
  const strictFormats = [
    'YYYY-MM-DD HH:mm:ss',
    'YYYY-MM-DD HH:mm',
    'YYYY-MM-DDTHH:mm:ss.SSSSSSZ',
    'YYYY-MM-DDTHH:mm:ss.SSSZ',
    'YYYY-MM-DDTHH:mm:ssZ',
    'YYYY-MM-DDTHH:mm:ss',
    'YYYY-MM-DDTHH:mmZ',
    'YYYY-MM-DDTHH:mm',
    'YYYY-MM-DD',
  ]
  for (let i = 0; i < strictFormats.length; i += 1) {
    try {
      const parsed = moment(text, strictFormats[i], true)
      if (isUsableMoment(parsed)) return parsed
    } catch (e) {
      // keep going — Z / offset tokens are unsafe in this moment-jalaali build
    }
  }
  try {
    const loose = moment(text)
    return isUsableMoment(loose) ? loose : null
  } catch (e) {
    return null
  }
}

/** Prefer clock digits from API strings like `2026-10-06 09:00` when formatters glitch. */
function clockFromDatetimeString(raw) {
  const text = String(raw == null ? '' : raw).trim()
  const m = text.match(/(?:T|\s)(\d{1,2}):(\d{2})(?::\d{2})?/)
  if (!m) return null
  const hh = String(m[1]).padStart(2, '0')
  const mm = String(m[2]).padStart(2, '0')
  return `${hh}:${mm}`
}

export function formatSessionDay(session, lang = 'fa') {
  const m = sessionMoment(session)
  if (!m || !m.isValid()) return '—'
  if (lang === 'fa') {
    return toFaDigits(m.format('jD'))
  }
  return m.format('D')
}

export function formatSessionMonth(session, lang = 'fa') {
  const m = sessionMoment(session)
  if (!m || !m.isValid()) return ''
  if (lang === 'fa') {
    return m.locale('fa').format('jMMMM')
  }
  return m.format('MMM')
}

export function formatSessionTitle(session, lang = 'fa') {
  const m = sessionMoment(session)
  if (!m || !m.isValid()) return ''
  if (lang === 'fa') {
    return toFaDigits(m.locale('fa').format('dddd · HH:mm'))
  }
  return m.format('dddd · HH:mm')
}

export function formatSessionListTitle(session, lang = 'fa') {
  const m = sessionMoment(session)
  if (!m || !m.isValid()) return ''
  if (lang === 'fa') {
    return toFaDigits(m.locale('fa').format('dddd jD jMMMM · HH:mm'))
  }
  return m.format('dddd D MMM · HH:mm')
}

export function formatSessionMeta(session, doctor, lang = 'fa') {
  const name = doctorDisplayName(doctor || (session && session.doctor), lang)
  const online = lang === 'fa' ? 'آنلاین' : 'Online'
  const status = sessionStatusLabel(session, lang)
  return [name, online, status].filter(Boolean).join(' · ')
}

export function sessionStatusLabel(session, lang = 'fa') {
  if (!session) return ''
  const start = sessionInstant(session)
  if (sessionStateKey(session) === 'date_fixed' && start && start.isValid()
      && start.clone().add(Number(session.duration) || 45, 'minutes').isBefore(moment())) {
    return lang === 'fa' ? 'زمان جلسه گذشته؛ نتیجه ثبت نشده' : 'Session time passed; outcome not recorded'
  }
  if (session.can_enter) {
    return lang === 'fa' ? 'آماده ورود' : 'Ready to join'
  }
  const state = session.state || session.status || ''
  const mapFa = {
    date_fixed: 'تأییدشده',
    doctor_assigned: 'در انتظار زمان',
    reserved: 'زمان رزروشده',
    pending: 'در انتظار',
    used: 'برگزار شده',
    canceled: 'لغو شده',
    cancelled: 'لغو شده',
    done: 'برگزار شده',
    completed: 'برگزار شده',
  }
  const mapEn = {
    date_fixed: 'Confirmed',
    doctor_assigned: 'Awaiting schedule',
    reserved: 'Reserved',
    pending: 'Pending',
    used: 'Completed',
    canceled: 'Cancelled',
    cancelled: 'Cancelled',
    done: 'Completed',
    completed: 'Completed',
  }
  const table = lang === 'fa' ? mapFa : mapEn
  return table[state] || state || (lang === 'fa' ? 'ثبت‌شده' : 'Scheduled')
}

export function isPastSession(session) {
  // Held / cancelled sessions are never "upcoming", even if fixed_date is still ahead.
  if (TERMINAL_SESSION_STATES[sessionStateKey(session)]) return true
  if (!isUpcomingSessionState(session)) return true
  // The server can admit a scheduled session after its start. Keeping it in the
  // current-session list does not grant entry; admission still checks the API.
  if (session && session.can_enter === true) return false
  const end = parseMomentSafe(session && (session.to_date_time_iso || session.to_date_time_g || session.ends_at))
  if (end && end.isValid()) return end.isBefore(moment())
  const m = sessionInstant(session)
  // doctor_assigned may have no clock yet — still eligible as upcoming.
  if (!m || !m.isValid()) return false
  return m.isBefore(moment())
}

export function fetchAllSessions(body = {}) {
  return portalRequest('session', 'getSessions', '', {
    orderBy: 'session_date:asc',
    ...body,
  }).then((resp) => {
    const payload = portalPayload(resp)
    if (Array.isArray(payload)) return payload
    return (payload && payload.sessions) || []
  })
}

/** One explicit server page; history must not silently discard its paginator. */
export function fetchSessionPage(page = 1, body = {}) {
  const value = Number(page)
  const requestedPage = Number.isSafeInteger(value) && value > 0 ? value : 1
  return portalRequest('session', 'getSessionPage', {page: requestedPage}, {
    orderBy: 'session_date:desc', ...body,
  }).then(careReceipt).then((payload) => {
    const pagination = payload && payload.pagination
    if (!Array.isArray(payload.sessions) || !pagination
        || !Number.isSafeInteger(Number(pagination.current_page)) || Number(pagination.current_page) < 1
        || !Number.isSafeInteger(Number(pagination.total_pages)) || Number(pagination.total_pages) < 0) {
      throw new Error('SESSION_HISTORY_RESPONSE_INVALID')
    }
    return {sessions: payload.sessions, pagination: {
      ...pagination, current_page: Number(pagination.current_page), total_pages: Math.max(1, Number(pagination.total_pages)),
    }}
  })
}

/** An explicit owned ID is resolved directly, independently of history pages. */
export function fetchOwnedSession(sessionId) {
  if (!hasAuthToken()) return Promise.reject(new Error('AUTH_REQUIRED'))
  const id = String(sessionId == null ? '' : sessionId).trim()
  if (!/^\d+$/.test(id)) return Promise.resolve(null)
  return portalRequest('session', 'getSession', {id}).then(careReceipt).then((session) => {
    if (!session || String(session.id) !== id) throw new Error('SESSION_RESPONSE_INVALID')
    return session
  })
}

/* ---------- Authoritative Phase 1 care context ---------- */

const SESSION_GRANTS = Object.create(null)

export function fetchActiveCare(context = {}) {
  if (!hasAuthToken()) return Promise.resolve(null)
  const params = {}
  const keys = ['userSessionId', 'user_session_id', 'sessionId', 'id']
  const present = context && typeof context === 'object' ? keys.filter(name => Object.prototype.hasOwnProperty.call(context, name)) : []
  for (const key of present) {
    const raw = context[key]
    const value = typeof raw === 'string' || typeof raw === 'number' ? String(raw) : ''
    if (!/^\d+$/.test(value) || !Number.isSafeInteger(Number(value)) || Number(value) <= 0) {
      return Promise.reject(new Error('CARE_SESSION_ID_INVALID'))
    }
    if (params.user_session_id && params.user_session_id !== String(Number(value))) return Promise.reject(new Error('CARE_SESSION_ID_INVALID'))
    params.user_session_id = String(Number(value))
  }
  return portalRequest('care', 'activeCare', params).then((resp) => {
    const payload = careReceipt(resp)
    if (!Object.prototype.hasOwnProperty.call(payload, 'episode')
        || (payload.episode !== null && (!payload.episode || typeof payload.episode !== 'object' || !payload.episode.id))) {
      throw new Error('CARE_RESPONSE_INVALID')
    }
    return payload.episode
  })
}

export function fetchCareEpisode(episodeId) {
  if (!hasAuthToken() || !episodeId) return Promise.resolve(null)
  return portalRequest('care', 'careEpisode', {episodeId}).then((resp) => {
    const payload = portalPayload(resp)
    return payload && payload.episode ? payload.episode : null
  })
}

export function fetchCareEpisodesHistory(page = 1) {
  if (!hasAuthToken()) return Promise.resolve({data: [], current_page: 1, last_page: 1})
  return portalRequest('care', 'careEpisodesHistory', {page}).then((resp) => {
    const payload = portalPayload(resp)
    if (Array.isArray(payload)) return {data: payload, current_page: 1, last_page: 1}
    return payload || {data: [], current_page: 1, last_page: 1}
  })
}

export function fetchCareClosureStatus(episodeId) {
  return portalRequest('care', 'careClosureStatus', {episodeId}).then(portalPayload)
}

export function requestCareClosure(episodeId, closeReason, idempotencyKey = null) {
  return portalRequest('care', 'requestCareClosure', {episodeId}, {
    close_reason: closeReason,
    idempotency_key: idempotencyKey || null,
  }).then(portalPayload)
}

export function confirmCareClosure(closureRequestId) {
  return portalRequest('care', 'confirmCareClosure', {closureRequestId}, {}).then(portalPayload)
}

export function fetchPrivateLetter(episodeId) {
  return portalRequest('care', 'getPrivateLetter', {episodeId}).then(portalPayload)
}

export function recordPrivateLetter(episodeId, payload) {
  return portalRequest('care', 'recordPrivateLetter', {episodeId}, payload || {}).then(portalPayload)
}

export function skipPrivateLetter(episodeId) {
  return portalRequest('care', 'skipPrivateLetter', {episodeId}, {}).then(portalPayload)
}

export function deletePrivateLetter(episodeId) {
  return portalRequest('care', 'deletePrivateLetter', {episodeId}, {}).then(portalPayload)
}

export function deliverPrivateLetter(episodeId) {
  return portalRequest('care', 'deliverPrivateLetter', {episodeId}, {}).then(portalPayload)
}

export function fetchCareConsent(episodeId) {
  return portalRequest('care', 'careConsent', {episodeId}).then(careReceipt).then((receipt) => {
    if (!['NOT_ACCEPTED', 'ACCEPTED', 'WITHDRAWN', 'SUPERSEDED'].includes(receipt.state)) throw new Error('CONSENT_RESPONSE_INVALID')
    return receipt
  })
}

export function acceptCareConsent(episodeId, version, contentHash) {
  return portalRequest('care', 'acceptCareConsent', {episodeId}, {version, content_hash: contentHash}).then(careReceipt).then((receipt) => {
    if (receipt.state !== 'ACCEPTED') throw new Error('CONSENT_ACCEPTANCE_NOT_CONFIRMED')
    return receipt
  })
}

export function withdrawCareConsent(episodeId) {
  return portalRequest('care', 'withdrawCareConsent', {episodeId}, {}).then(careReceipt).then((receipt) => {
    if (receipt.state !== 'WITHDRAWN') throw new Error('CONSENT_WITHDRAWAL_NOT_CONFIRMED')
    return receipt
  })
}

export function fetchFirstSessionOperationalReadiness(episodeId) {
  return portalRequest('care', 'firstSessionOperationalReadiness', {episodeId}).then(portalPayload)
}

export function saveCareEngagementBaseline(episodeId, answers) {
  return portalRequest('care', 'saveEngagementBaseline', {episodeId}, answers || {}).then(careReceipt).then((receipt) => {
    if (!['COMPLETED', 'IN_PROGRESS', 'SKIPPED'].includes(receipt.state) || receipt.blocking_effect !== 'NONE') throw new Error('ENGAGEMENT_RESPONSE_INVALID')
    return receipt
  })
}

export function skipCarePrivateLetterOffer(episodeId) {
  return portalRequest('care', 'skipPrivateLetterOffer', {episodeId}, {}).then(portalPayload)
}

export function createCareSupportCase(episodeId, category, summary = '') {
  return portalRequest('care', 'createCareSupportCase', {episodeId}, {category, summary}).then(portalPayload)
}

export function fetchNextSessionBooking(episodeId) {
  return portalRequest('care', 'nextSessionOverview', {episodeId}).then(careReceipt).then((data) => {
    if (typeof data.eligible !== 'boolean') throw new Error('NEXT_SESSION_RESPONSE_INVALID')
    return data
  })
}

export function holdNextSession(episodeId, slotId, idempotencyKey) {
  return portalRequest('care', 'holdNextSession', {episodeId}, {
    slot_id: slotId,
    idempotency_key: idempotencyKey,
  }).then(careReceipt)
}

export function reserveNextSessionCredit(episodeId, slotId, idempotencyKey) {
  return portalRequest('care', 'reserveNextSessionCredit', {episodeId}, {
    slot_id: slotId,
    idempotency_key: idempotencyKey,
  }).then(careReceipt)
}

export function confirmNextSessionCredit(episodeId, holdId) {
  return portalRequest('care', 'confirmNextSessionCredit', {episodeId, holdId}, {}).then(careReceipt)
}

export function checkoutNextSession(episodeId, holdId, reviewId) {
  return portalRequest('care', 'checkoutNextSession', {episodeId, holdId}, reviewId ? {review_id:reviewId} : {}).then(careReceipt)
}

export function returnNextSessionPayment(episodeId, holdId) {
  return portalRequest('care', 'returnNextSessionPayment', {episodeId, holdId}, {}).then(careReceipt)
}

export function releaseNextSessionHold(episodeId, holdId) {
  return portalRequest('care', 'releaseNextSessionHold', {episodeId, holdId}, {}).then(careReceipt)
}

export function fetchCareClinicalResponse(episodeId) {
  return portalRequest('care', 'clinicalResponse', {episodeId}).then(careReceipt)
}

export function fetchActiveClinicalResponse() {
  return fetchActiveCare().then((episode) => {
    if (!episode || !episode.id) return {episode: null, reaction: null, rematch: null}
    return fetchCareClinicalResponse(episode.id).then((data) => Object.assign({episode}, data || {}))
  })
}

export function decideClinicalRematch(rematchId, decision) {
  return portalRequest('care', 'clinicalRematchDecision', {rematchId}, {decision}).then(careReceipt)
}

export function startClinicalRematch(rematchId, snapshotId, idempotencyKey) {
  return portalRequest('care', 'clinicalRematchMatch', {rematchId}, {
    client_requirement_snapshot_id: snapshotId,
    idempotency_key: idempotencyKey || null,
  }).then(careReceipt)
}

export function createActiveCareSupportCase(category, summary = '') {
  return fetchActiveCare().then((episode) => {
    if (!episode || !episode.id) {
      const err = new Error('CARE_CONTEXT_REQUIRED')
      err.code = 'CARE_CONTEXT_REQUIRED'
      throw err
    }
    return createCareSupportCase(episode.id, category, summary)
  })
}

export function requestSessionAdmission(sessionId) {
  if (!hasAuthToken() || sessionId == null) return Promise.reject(new Error('AUTH_REQUIRED'))
  return portalRequest('care', 'sessionAdmission', {sessionId}, {}).then((resp) => {
    const payload = portalPayload(resp)
    if (!payload || payload.allowed !== true) {
      const err = new Error((payload && payload.reason_code) || 'SESSION_ADMISSION_DENIED')
      err.code = (payload && payload.reason_code) || 'SESSION_ADMISSION_DENIED'
      err.blockerCodes = (payload && payload.blocker_codes) || []
      throw err
    }
    SESSION_GRANTS[String(sessionId)] = payload
    return payload
  })
}

export function admissionJoinUrl(admission) {
  const grant = admission && admission.grant
  if (!grant) return null
  return grant.join_url || grant.meetingURL || null
}

export function cachedSessionAdmission(sessionId) {
  return sessionId == null ? null : (SESSION_GRANTS[String(sessionId)] || null)
}

export function fetchAssignRecords() {
  return portalRequest('user', 'getAssignRecord').then((resp) => {
    const data = resp.data || {}
    return data.assignDoctors || data.assign_doctors || []
  })
}

export function fetchOutcomeChart(assignRecordId) {
  return portalRequest('user', 'getMyoutcomeChart', {assignRecordId}).then((resp) => resp.data || {})
}

/** Build a neutral, non-numeric client path from server qualitative direction keys. */
export function buildQualitativePathModel(path = {}) {
  const source = Array.isArray(path.qualitativePoints) ? path.qualitativePoints : []
  const maxPoints = Math.max(source.length, 1)
  let y = 110
  const points = source.map((item, index) => {
    if (index > 0) {
      if (item.directionKey === 'up') y -= 18
      else if (item.directionKey === 'down') y += 18
      y = Math.max(45, Math.min(165, y))
    }
    return {
      sessionNumber: Number(item.sessionNumber),
      directionKey: item.directionKey,
      x: source.length <= 1 ? 32 : 32 + (index * 260 / (maxPoints - 1)),
      y,
    }
  })
  return {
    points,
    linePoints: points.map((p) => `${p.x},${p.y}`).join(' '),
    gaps: Array.isArray(path.gaps) ? path.gaps.map(Number) : [],
    narrativeKey: path.narrativeKey || 'client.path.no_measurements',
    rawValuesIncluded: false,
  }
}

/* ---------- Money / packages (legacy panel twins) ---------- */

export function walletSessionCredit(profile) {
  const wallet = profile && profile.wallet
  if (!wallet) return 0
  const nums = [wallet.total, wallet.global_packages, wallet.global_available, wallet.balance]
    .map((v) => Number(v))
    .filter((n) => Number.isFinite(n))
  if (!nums.length) return 0
  return Math.max(0, ...nums)
}

/** Active-care clients choose their next session with their matched therapist. */
export function reserveSessionRoute(lang = 'fa') {
  return {name: 'portalNextSessionBooking', params: {lang: lang || 'fa'}}
}

export function fetchUserFactors(page = 1) {
  if (!hasAuthToken()) return Promise.resolve([])
  return portalRequest('factor', 'getUserFactors', {page}).then((resp) => {
    const data = resp.data || {}
    return Array.isArray(data.factors) ? data.factors : []
  })
}

/** Paginated invoice history; retain the array-only adapter above for existing callers. */
export function fetchUserFactorPage(page = 1) {
  const requested = Number(page)
  const current = Number.isSafeInteger(requested) && requested > 0 ? requested : 1
  if (!hasAuthToken()) return Promise.resolve({factors: [], pagination: {current_page: 1, total_pages: 1}})
  return portalRequest('factor', 'getUserFactors', {page: current}).then((resp) => {
    const data = careReceipt(resp)
    const pagination = data.pagination
    if (!Array.isArray(data.factors) || !pagination || !Number.isInteger(Number(pagination.current_page))
        || Number(pagination.current_page) < 1 || !Number.isInteger(Number(pagination.total_pages))
        || Number(pagination.total_pages) < 0) {
      throw new Error('PAYMENT_HISTORY_RESPONSE_INVALID')
    }
    return {factors: data.factors, pagination: {
      ...pagination, current_page: Number(pagination.current_page), total_pages: Math.max(1, Number(pagination.total_pages)),
    }}
  })
}

export function fetchFactor(factorID) {
  if (!hasAuthToken() || factorID == null) return Promise.resolve(null)
  return portalRequest('factor', 'getFactor', {factorID}).then((resp) => (resp.data && resp.data.factor) || null)
}

export function fetchPackagesByGrade(grade) {
  if (!hasAuthToken() || grade == null || grade === '') return Promise.resolve([])
  // API only accepts A|B|C (case-sensitive uppercase).
  const normalized = String(grade).trim().toUpperCase()
  if (!/^[ABC]$/.test(normalized)) return Promise.resolve([])
  return portalRequest('packages', 'getPackagesByGrade', {}, {grade: normalized}).then((resp) => {
    const data = resp.data || {}
    return Array.isArray(data.packages) ? data.packages : []
  }).catch(() => [])
}

export function fetchAllPackages(page = 1, type = 'session') {
  if (!hasAuthToken()) return Promise.resolve([])
  return portalRequest('packages', 'getAllPackages', {page}, {type}).then((resp) => {
    const data = resp.data || {}
    return Array.isArray(data.packages) ? data.packages : []
  })
}

/** The server resolves the current therapist and the configured session rate. */
export function fetchPortalPackageCatalog() {
  if (!hasAuthToken()) return Promise.resolve({packages: [], session_rate: null, therapist: null})
  return portalRequest('packages', 'getAllPackages', {page: 1}, {type: 'session'}).then((resp) => {
    const data = portalPayload(resp)
    return {
      packages: Array.isArray(data.packages) ? data.packages : [],
      session_rate: data.session_rate || null,
      therapist: data.therapist || null,
    }
  })
}

export function fetchPortalPackages() {
  return fetchPortalPackageCatalog().then((catalog) => catalog.packages)
}

export function addPackageToCart(packageId, priceRecordId) {
  return portalRequest('cart', 'addToCart', '', {
    package_id: packageId,
    addone: false,
    care_package_purchase: true,
    price_record_id: priceRecordId,
  }).then((resp) => {
    const cart = resp.data && resp.data[0]
    if (cart) {
      store.dispatch('setCart', {key: 'cart', data: [cart]})
    }
    return cart || resp.data
  })
}

export function packageSessionCount(pkg) {
  if (!pkg) return 0
  const n = Number(pkg.session_count ?? pkg.sessions ?? pkg.count ?? pkg.qty ?? 0)
  if (Number.isFinite(n) && n > 0) return n
  // Fallback: parse from title like "بسته ۵ جلسه‌ای"
  const m = String(pkg.title || pkg.en_title || '').match(/(\d+)/)
  return m ? Number(m[1]) : 0
}

export function formatMoneyAmount(amount, currency, lang = 'fa') {
  const n = Number(amount)
  if (!Number.isFinite(n)) return '—'
  const code = String(currency || '').trim().toLowerCase()
  // Product convention: legacy 'rial' / IRT amounts are Toman-denominated (no ÷10).
  // True IRR (rare) is Rial units and displays as Toman via ÷10.
  let display = n
  let toman = false
  if (code === 'irr') {
    display = Math.round(n / 10)
    toman = true
  } else if (code === 'rial' || code === 'irt' || code === 'toman' || code === '') {
    display = Math.round(n)
    toman = true
  }
  const formatted = Math.round(display).toLocaleString(lang === 'fa' ? 'fa-IR' : 'en-US')
  if (lang === 'fa') {
    if (toman) return `${toFaDigits(formatted)} تومان`
    if (code === 'dollar' || code === 'usd') return `${toFaDigits(formatted)} دلار`
    if (code === 'euro' || code === 'eur') return `${toFaDigits(formatted)} یورو`
    return `${toFaDigits(formatted)} تومان`
  }
  if (code === 'dollar' || code === 'usd') return `$${Number(n).toFixed(2)}`
  if (code === 'euro' || code === 'eur') return `€${Number(n).toFixed(2)}`
  return `${formatted} Toman`
}

export function factorTitle(factor, lang = 'fa') {
  if (!factor) return ''
  const pkgs = Array.isArray(factor.packages) ? factor.packages : []
  if (pkgs.length === 1) {
    return lang === 'fa' ? (pkgs[0].title || pkgs[0].en_title) : (pkgs[0].en_title || pkgs[0].title)
  }
  if (pkgs.length > 1) {
    return lang === 'fa' ? `${toFaDigits(pkgs.length)} محصول` : `${pkgs.length} items`
  }
  return lang === 'fa' ? 'فاکتور' : 'Invoice'
}

export function factorStateLabel(factor, lang = 'fa') {
  if (!factor) return ''
  const state = factor.state
  if (state === 'paid') return lang === 'fa' ? 'پرداخت‌شده' : 'Paid'
  if (factor.payment_verification_pending === true) return lang === 'fa' ? 'تأیید پرداخت نیاز به پیگیری دارد' : 'Payment verification needs follow-up'
  if (state === 'partially_paid') return lang === 'fa' ? 'پرداخت ناقص' : 'Partially paid'
  if (state === 'submit' && factor.canBePaid) return lang === 'fa' ? 'در انتظار پرداخت' : 'Awaiting payment'
  if (state === 'submit') return lang === 'fa' ? 'فاکتور ثبت شده؛ پرداخت تأیید نشده' : 'Invoice submitted; payment unconfirmed'
  return state || (lang === 'fa' ? 'نامشخص' : 'Unknown')
}

/** Read the owner's booking projection; never initiate or verify a payment here. */
export function fetchActiveBookingStatus() {
  if (!hasAuthToken()) return Promise.reject(new Error('AUTH_REQUIRED'))
  return portalRequest('matchmaking_v2', 'getActiveBooking').then(careReceipt).then((data) => {
    const statuses = ['NONE', 'HOLD_ACTIVE', 'AWAITING_PAYMENT', 'PROCESSING', 'CONFIRMED', 'PAYMENT_LATE', 'PAYMENT_FAILED', 'CANCELLED', 'MANUAL_SCHEDULING_REQUIRED']
    if (!data || !Object.prototype.hasOwnProperty.call(data, 'booking') || !Object.prototype.hasOwnProperty.call(data, 'hold') || !statuses.includes(data.client_status)) {
      throw new Error('BOOKING_STATUS_RESPONSE_INVALID')
    }
    return data
  })
}

export function formatFactorDate(factor, lang = 'fa') {
  const raw = factor && (factor.submitted_at || factor.date || factor.created_at)
  if (!raw) return ''
  const m = moment(raw)
  if (!m.isValid()) return String(raw)
  if (lang === 'fa') {
    return toFaDigits(m.locale('fa').format('jD jMMMM · HH:mm'))
  }
  return m.format('D MMM · HH:mm')
}

export function doctorSessionRate(doctor, lang = 'fa') {
  if (!doctor) return ''
  const price = doctor.session_price
    ?? doctor.price
    ?? (doctor.user && doctor.user.session_price)
    ?? null
  const currency = doctor.currency || doctor.currency_code || 'toman'
  if (price == null) return ''
  return formatMoneyAmount(price, currency, lang)
}

/* ---------- Canonical Phase 2 treatment measurement ---------- */

const LAST_ASSESS_KEY = 'rb_portal_last_assessment'

export function fetchPendingTreatmentAssessments() {
  if (!hasAuthToken()) return Promise.resolve([])
  return portalRequest('treatment_assessment', 'pendingTreatmentAssessments').then((resp) => {
    const payload = portalPayload(resp)
    return Array.isArray(payload.items) ? payload.items : []
  })
}

export function fetchTreatmentAssessmentForm(sessionId, instrument) {
  if (!hasAuthToken() || sessionId == null || !instrument) return Promise.reject(new Error('ASSESSMENT_CONTEXT_REQUIRED'))
  return portalRequest('treatment_assessment', 'treatmentAssessmentForm', {sessionId, instrument: String(instrument).toUpperCase()}).then(portalPayload)
}

export function submitTreatmentAssessment(form, ratings) {
  if (!form || !form.episodeId || !form.participantId || form.sessionId == null) return Promise.reject(new Error('ASSESSMENT_CONTEXT_REQUIRED'))
  const instrument = String(form.instrument || '').toUpperCase()
  const name = instrument === 'ORS' || instrument === 'CORS' ? 'submitTreatmentOrs' : 'submitTreatmentSrs'
  return portalRequest('treatment_assessment', name, {}, {
    episodeId: form.episodeId,
    participantId: form.participantId,
    sessionId: form.sessionId,
    instrument,
    contentVersion: form.contentVersion,
    items: (ratings || []).map((v) => Number(v)),
    channel: 'portal',
    clientCompletedAt: new Date().toISOString(),
  }).then(portalPayload)
}

export function amendTreatmentAssessment(assessmentId, ratings, amendmentReason) {
  return portalRequest('treatment_assessment', 'amendTreatmentAssessment', {assessmentId}, {items:(ratings || []).map(Number), amendmentReason}).then(portalPayload)
}

export function skipTreatmentAssessment(form) {
  if (!form || !form.episodeId || !form.participantId || form.sessionId == null || !form.instrument) return Promise.reject(new Error('ASSESSMENT_CONTEXT_REQUIRED'))
  return portalRequest('treatment_assessment', 'skipTreatmentAssessment', {}, {
    episodeId: form.episodeId,
    participantId: form.participantId,
    sessionId: form.sessionId,
    instrument: String(form.instrument).toUpperCase(),
  }).then(portalPayload)
}

export function fetchTreatmentPath(episodeId) {
  if (!hasAuthToken() || !episodeId) return Promise.resolve({qualitativePoints:[], gaps:[], rawValuesIncluded:false})
  return portalRequest('treatment_assessment', 'treatmentPath', {episodeId}).then(portalPayload)
}

const TREATMENT_INELIGIBLE_CODES = [
  'TREATMENT_SESSION_INELIGIBLE',
  'TREATMENT_SESSION_NOT_FOUND',
  'TREATMENT_OBLIGATION_NOT_FOUND',
  'TREATMENT_PARTICIPANT_SESSION_FORBIDDEN',
  'TREATMENT_ASSESSMENT_OUT_OF_SEQUENCE',
  'TREATMENT_ORS_WINDOW_CLOSED',
  'TREATMENT_SRS_WINDOW_EXPIRED',
  'TREATMENT_SRS_NOT_YET_DUE',
]

export function portalReasonCode(err) {
  const data = err && err.response && err.response.data
  if (!data) return ''
  if (data.data && data.data.reason_code) return String(data.data.reason_code)
  if (typeof data.message === 'string' && /^[A-Z][A-Z0-9_]+$/.test(data.message)) return data.message
  return ''
}

export function treatmentAssessmentErrorKey(err) {
  const code = portalReasonCode(err)
  if (code === 'TREATMENT_SESSION_INELIGIBLE' || code === 'TREATMENT_SESSION_NOT_FOUND') {
    return 'intake.treatment.sessionIneligible'
  }
  if (code === 'TREATMENT_OBLIGATION_NOT_FOUND' || code === 'TREATMENT_PARTICIPANT_SESSION_FORBIDDEN') {
    return 'intake.treatment.noPendingAssessment'
  }
  if (code === 'TREATMENT_CONTENT_UNAVAILABLE_FOR_SCOPE') return 'intake.treatment.contentUnavailable'
  if (code === 'TREATMENT_SRS_NOT_YET_DUE') return 'intake.treatment.srsNotDue'
  if (code === 'TREATMENT_SRS_WINDOW_EXPIRED') return 'intake.treatment.srsWindowExpired'
  if (code === 'TREATMENT_ORS_WINDOW_CLOSED') return 'intake.treatment.orsWindowClosed'
  if (code === 'TREATMENT_ASSESSMENT_OUT_OF_SEQUENCE') return 'intake.treatment.outOfSequence'
  if (code === 'TREATMENT_ALREADY_SKIPPED') return 'intake.treatment.orsAlreadySkipped'
  if (code === 'TREATMENT_PARTICIPANT_FORBIDDEN') return 'intake.treatment.noPendingAssessment'
  return 'intake.treatment.submitError'
}

function isAllianceInstrument(instrument) {
  const kind = String(instrument || '').toUpperCase()
  return kind === 'SRS' || kind === 'CSRS'
}

function assessmentRouteName(instrument) {
  return isAllianceInstrument(instrument) ? 'intakeOutcomeSrs' : 'intakeOutcomeOrs'
}

/** Server pending list is already sequenced to the single next step. */
export function pickNextPendingAssessment(items) {
  const rows = Array.isArray(items) ? items : []
  return rows[0] || null
}

function loadFirstPendingForm(preferredInstrument) {
  const preferred = String(preferredInstrument || '').toUpperCase()
  return fetchPendingTreatmentAssessments().then((items) => {
    const next = pickNextPendingAssessment(items)
    if (!next) return null
    // Prefer the server-selected next step; only fall back to preferred instrument if it matches.
    const kind = preferred && String(next.instrument).toUpperCase() === preferred
      ? preferred
      : String(next.instrument).toUpperCase()
    const sessionId = preferred && String(next.instrument).toUpperCase() !== preferred
      ? next.sessionId
      : next.sessionId
    return fetchTreatmentAssessmentForm(sessionId, kind)
  })
}

export function resolvePendingTreatmentAssessment(preferredSessionId, instrument = 'ORS') {
  const kind = String(instrument).toUpperCase()
  if (preferredSessionId == null) return loadFirstPendingForm(kind)
  return fetchTreatmentAssessmentForm(preferredSessionId, kind).catch((err) => {
    if (!TREATMENT_INELIGIBLE_CODES.includes(portalReasonCode(err))) throw err
    return loadFirstPendingForm(kind).then((form) => {
      if (form) return form
      throw err
    })
  })
}

export function nextAssessmentRoute(lang, pendingRow) {
  if (!pendingRow) return null
  return {
    name: assessmentRouteName(pendingRow.instrument),
    params: {lang: lang || 'fa'},
    query: {sessionId: String(pendingRow.sessionId)},
  }
}

export function listPendingAssessments() {
  return fetchPendingTreatmentAssessments().then((items) => items.map((row) => ({
    id: row.obligationId,
    sessionId: row.sessionId,
    sessionNumber: row.sessionNumber,
    episodeId: row.episodeId,
    instrument: row.instrument,
    kind: String(row.instrument || '').toLowerCase(),
    status: row.status,
  })))
}

export function rememberLastAssessment(meta) {
  try { sessionStorage.setItem(LAST_ASSESS_KEY, JSON.stringify(meta || {})) } catch (e) { /* ignore */ }
}

export function readLastAssessment() {
  try { const raw = sessionStorage.getItem(LAST_ASSESS_KEY); return raw ? JSON.parse(raw) : null } catch (e) { return null }
}

/* ---------- Live session join (legacy SessionCard) ---------- */

const ACTIVE_SESSION_KEY = 'rb_portal_active_session'

export function rememberActiveSession(session) {
  try {
    if (!session) {
      sessionStorage.removeItem(ACTIVE_SESSION_KEY)
      return
    }
    sessionStorage.setItem(ACTIVE_SESSION_KEY, JSON.stringify({
      id: session.id,
      can_enter: !!session.can_enter,
      fixed_date_time_g: session.fixed_date_time_g,
      to_date_time_g: session.to_date_time_g,
      fixed_date_time_iso: session.fixed_date_time_iso,
      to_date_time_iso: session.to_date_time_iso,
      doctor: session.doctor || null,
      alocam_session: session.alocam_session ? {available: !!session.alocam_session.available} : null,
      state: session.state || session.status || null,
    }))
  } catch (e) { /* ignore */ }
}

export function readActiveSession() {
  try {
    const raw = sessionStorage.getItem(ACTIVE_SESSION_KEY)
    return raw ? JSON.parse(raw) : null
  } catch (e) {
    return null
  }
}

export function sessionJoinUrl(session) {
  if (!session || session.id == null) return null
  return admissionJoinUrl(cachedSessionAdmission(session.id))
}


export function sessionHasJoinLink(session) {
  return !!sessionJoinUrl(session)
}

export function resolveJoinableSession(preferredId) {
  const want = preferredId != null ? String(preferredId).trim() : null

  const pick = (list) => {
    const arr = Array.isArray(list) ? list.filter(Boolean) : []
    if (want) {
      const hit = arr.find((s) => String(s.id) === want)
      return hit || null
    }
    const enterable = arr.find((s) => s.can_enter)
    if (enterable) return enterable
    return arr[0] || null
  }

  if (!hasAuthToken()) {
    return Promise.resolve(null)
  }

  if (want !== null) {
    if (!/^\d+$/.test(want)) return Promise.resolve(null)
    // An explicit URL always resolves that owned session from the server.
    return portalRequest('session', 'getSession', {id: want}).then(portalPayload).then((session) => {
      if (!session || String(session.id) !== want || !isUpcomingSessionState(session) || isPastSession(session)) return null
      rememberActiveSession(session)
      return session
    }).catch(() => null)
  }

  return fetchProfile()
      .catch(() => getCachedProfile())
      .then((profile) => {
        const upcoming = upcomingFromProfile(profile)
        let session = pick(upcoming)
        if (session) {
          rememberActiveSession(session)
          return session
        }
        return fetchAllSessions({orderBy: 'session_date:asc'})
            .then((sessions) => {
              const future = (sessions || []).filter((s) => isUpcomingSessionState(s) && !isPastSession(s))
              session = pick(future)
              if (session) rememberActiveSession(session)
              return session
            })
            .catch(() => null)
      })
}

/** Refresh profile/sessions and return updated session for polling can_enter. */
export function refreshJoinableSession(preferredId) {
  return resolveJoinableSession(preferredId)
}

export function countdownToSession(session) {
  if (!session) return null
  const target = sessionInstant(session)
  if (!target) return null
  const ms = target.diff(moment())
  if (ms <= 0) return { overdue: true, label: '00:00:00', ms: 0 }
  const duration = moment.duration(ms)
  const hours = Math.floor(duration.asHours()).toString().padStart(2, '0')
  const minutes = Math.abs(duration.minutes()).toString().padStart(2, '0')
  const seconds = Math.abs(duration.seconds()).toString().padStart(2, '0')
  return { overdue: false, label: `${hours}:${minutes}:${seconds}`, ms }
}

/* ---------- Booking change + rematch + pay (Phase 5) ---------- */

/**
 * Phase 5 uses server-owned V2 booking truth only. Legacy journey/session mutation,
 * direct matcher calls, browser payment caches and factor-pay retries are not authority.
 */
export function fetchBookingOperations(opts = {}) {
  if (!hasAuthToken()) return Promise.resolve({booking: null, client_status: 'AUTH_REQUIRED', policy: {}})
  const params = {}
  const sessionId = opts.userSessionId || opts.sessionId || opts.id
  if (sessionId !== undefined && sessionId !== null && String(sessionId) !== '' && /^\d+$/.test(String(sessionId))) {
    params.user_session_id = String(sessionId)
  }
  return portalRequest('care', 'bookingOperations', params).then(portalPayload)
}

export function fetchBookingCancellationPreview(bookingId) {
  if (!hasAuthToken() || !bookingId) return Promise.resolve(null)
  return portalRequest('care', 'bookingCancellationPreview', {bookingId}).then(portalPayload)
}

export function cancelUnpaidBookingOperation(bookingId) {
  if (!hasAuthToken() || !bookingId) return Promise.reject(new Error('BOOKING_ID_REQUIRED'))
  return portalRequest('care', 'cancelUnpaidBookingOperation', {bookingId}, {}).then(portalPayload)
}

export function cancelConfirmedBookingOperation(bookingId) {
  if (!hasAuthToken() || !bookingId) return Promise.reject(new Error('BOOKING_ID_REQUIRED'))
  return portalRequest('care', 'cancelConfirmedBookingOperation', {bookingId}, {}).then(portalPayload)
}

export function fetchCareSessionCancellation(sessionId) {
  if (!hasAuthToken() || !/^\d+$/.test(String(sessionId))) return Promise.reject(new Error('SESSION_ID_REQUIRED'))
  return portalRequest('care', 'careSessionCancellation', {sessionId: String(sessionId)}).then(portalPayload)
}

export function cancelCareSession(sessionId) {
  if (!hasAuthToken() || !/^\d+$/.test(String(sessionId))) return Promise.reject(new Error('SESSION_ID_REQUIRED'))
  return portalRequest('care', 'cancelCareSession', {sessionId: String(sessionId)}, {}).then(portalPayload)
}

export function fetchBookingReschedulePreview(bookingId) {
  if (!hasAuthToken() || !bookingId) return Promise.resolve(null)
  return portalRequest('care', 'bookingReschedulePreview', {bookingId}).then(portalPayload)
}

export function fetchCareSessionReschedulePreview(sessionId) {
  if (!hasAuthToken() || !/^\d+$/.test(String(sessionId))) return Promise.reject(new Error('SESSION_ID_REQUIRED'))
  return portalRequest('care', 'careSessionReschedulePreview', {sessionId: String(sessionId)}).then(portalPayload)
}

export function rescheduleCareSession(sessionId, slotId) {
  if (!hasAuthToken() || !/^\d+$/.test(String(sessionId))) return Promise.reject(new Error('SESSION_ID_REQUIRED'))
  return portalRequest('care', 'rescheduleCareSession', {sessionId: String(sessionId)}, {slot_id: String(slotId)}).then(portalPayload)
}

export function commitBookingReschedule(bookingId, slotId) {
  if (!hasAuthToken() || !bookingId) return Promise.reject(new Error('BOOKING_ID_REQUIRED'))
  if (slotId === undefined || slotId === null || String(slotId) === '') {
    return Promise.reject(new Error('SLOT_ID_REQUIRED'))
  }
  return portalRequest('care', 'commitBookingReschedule', {bookingId}, {
    new_slot_id: Number(slotId),
  }).then(portalPayload)
}

/** Therapist open calendar slots for preview-gated reschedule (not payment authority). */
export function fetchRescheduleDoctorSlots(doctorId) {
  if (!hasAuthToken() || !doctorId) return Promise.resolve([])
  return portalRequest('user', 'getDoctorTimes', {doctor: doctorId}).then((resp) => {
    const times = (resp && resp.data && resp.data.times) || []
    return Array.isArray(times) ? times : []
  })
}

export function rescheduleSlotMoment(slot) {
  if (!slot) return null
  return parseMomentSafe(slot.from_time || slot.date)
}

export function formatRescheduleSlotDayKey(slot, lang = 'fa') {
  const m = rescheduleSlotMoment(slot)
  if (!m) return ''
  if (lang === 'fa') return m.format('jYYYY-jMM-jDD')
  return m.format('YYYY-MM-DD')
}

export function formatRescheduleSlotDayLabel(slot, lang = 'fa') {
  const m = rescheduleSlotMoment(slot)
  if (!m) return '—'
  if (lang === 'fa') {
    return toFaDigits(m.locale('fa').format('dddd jD jMMMM'))
  }
  return m.locale('en').format('ddd D MMM')
}

export function formatRescheduleSlotTimeLabel(slot, lang = 'fa') {
  const raw = slot && (slot.from_time || slot.date)
  const fromRaw = clockFromDatetimeString(raw)
  const m = rescheduleSlotMoment(slot)
  const fromMoment = m && m.isValid() ? m.format('HH:mm') : null
  // Prefer clock digits from the API string (DatesAndTime emits `Y-m-d H:i`).
  const t = fromRaw || fromMoment || '—'
  if (t === '—') return t
  return lang === 'fa' ? toFaDigits(t) : t
}

export function formatRescheduleSlotLabel(slot, lang = 'fa') {
  const m = rescheduleSlotMoment(slot)
  const time = formatRescheduleSlotTimeLabel(slot, lang)
  if (!m) return time
  if (lang === 'fa') {
    return `${toFaDigits(m.locale('fa').format('jD jMMMM'))} · ${time}`
  }
  return `${m.locale('en').format('D MMM')} · ${time}`
}

export function formatRescheduleDeadline(iso, lang = 'fa') {
  if (!iso) return lang === 'fa' ? 'تعریف نشده' : 'Not defined'
  try {
    const m = parseMomentSafe(iso)
    if (!m) return String(iso)
    const time = m.format('HH:mm')
    if (lang === 'fa') {
      return `${toFaDigits(m.locale('fa').format('jD jMMMM'))} · ${toFaDigits(time)}`
    }
    return `${m.locale('en').format('D MMM YYYY')} · ${time}`
  } catch (e) {
    return String(iso)
  }
}

function paymentReturnIdentifiers(query = {}) {
  const payload = {}
  const aliases = {
    booking_id: ['booking_id', 'bookingId'], cart_id: ['cart_id', 'cartId', 'factor', 'factor_id', 'factorId'],
    payment_id: ['payment_id', 'paymentId', 'successful_payment', 'payment'],
  }
  for (const [name, keys] of Object.entries(aliases)) {
    for (const key of keys.filter(key => Object.prototype.hasOwnProperty.call(query, key))) {
      const value = query[key]
      if (!['string', 'number'].includes(typeof value) || String(value).trim() === '') throw new Error('PAYMENT_IDENTIFIERS_INVALID')
      const normalized = name === 'booking_id' ? String(value) : Number(value)
      if (name !== 'booking_id' && (!/^\d+$/.test(String(value)) || !Number.isSafeInteger(normalized) || normalized <= 0)) throw new Error('PAYMENT_IDENTIFIERS_INVALID')
      if (payload[name] !== undefined && payload[name] !== normalized) throw new Error('PAYMENT_IDENTIFIERS_INVALID')
      payload[name] = normalized
    }
  }
  return payload
}

/**
 * Resolve a result screen against server truth. Query parameters are identifiers only;
 * they can never declare success, amounts, refund entitlement, or booking state.
 */
export function resolvePaymentResult(query = {}) {
  if (!hasAuthToken()) return Promise.resolve(null)
  let identifiers
  try { identifiers = paymentReturnIdentifiers(query) } catch (error) { return Promise.reject(error) }
  const request = Object.keys(identifiers).length
    ? portalRequest('matchmaking_v2', 'paymentReturn', {}, identifiers)
    : portalRequest('matchmaking_v2', 'getActiveBooking', {}, {})

  return request.then(careReceipt).then((data) => {
    const booking = data && data.booking ? data.booking : data
    if (!booking || !booking.booking_id) {
      if (!Object.prototype.hasOwnProperty.call(data, 'booking') || data.booking !== null || !['NONE', 'HELD', 'HOLD_ACTIVE'].includes(data.client_status)) {
        throw new Error('BOOKING_STATUS_RESPONSE_INVALID')
      }
      return {kind: 'server', booking: null, clientStatus: (data && data.client_status) || 'NONE', source: 'SERVER'}
    }
    const status = booking.client_status || data.client_status || booking.state
    if (!['CONFIRMED', 'MANUAL_SCHEDULING_REQUIRED', 'AWAITING_PAYMENT', 'PROCESSING', 'PAYMENT_LATE', 'PAYMENT_FAILED', 'CANCELLED'].includes(status)) {
      throw new Error('BOOKING_STATUS_RESPONSE_INVALID')
    }
    return {
      kind: 'server',
      booking,
      clientStatus: status,
      source: 'SERVER',
    }
  })
}

export function paymentResultAmount(result) {
  const booking = result && result.booking
  if (!booking || booking.accepted_amount == null) return null
  const value = Number(booking.accepted_amount)
  return Number.isFinite(value) ? value : null
}

/** Booking code is the only client-safe receipt-like identifier exposed by this slice. */
export function paymentResultReceipt(result) {
  const booking = result && result.booking
  return booking && booking.booking_code ? String(booking.booking_code) : ''
}

export function bookingResultCurrency(result) {
  const booking = result && result.booking
  return booking && booking.accepted_currency ? booking.accepted_currency : null
}

export function bookingResultStatus(result) {
  if (!result) return 'UNKNOWN'
  return result.clientStatus || (result.booking && (result.booking.client_status || result.booking.state)) || 'UNKNOWN'
}

export function bookingResultCode(result) {
  const booking = result && result.booking
  return booking && booking.booking_code ? String(booking.booking_code) : ''
}

/* ---------- Purpose-bound support + therapist chat (Phase 6) ---------- */

export function fetchMessengerContacts() {
  if (!hasAuthToken()) return Promise.resolve([])
  return portalRequest('messenger', 'getContacts')
      .then((resp) => {
        const payload = messengerPayload(resp)
        return Array.isArray(payload.contacts) ? payload.contacts : []
      })
}

export function fetchMessagesByUser(userId, page = 1) {
  if (!hasAuthToken() || userId == null || userId === '') {
    return Promise.resolve({messages: [], conversationId: null, pagination: null, ok: false})
  }
  return portalRequest('messenger', 'getMessageByUser', {userId, page}, {})
      .then((resp) => {
        const data = messengerPayload(resp)
        if (!Array.isArray(data.messages) || !data.conversation || data.conversation.id == null) {
          throw new Error('CHAT_RESPONSE_INVALID')
        }
        const messages = data.messages
        const conversationId = data.conversation && data.conversation.id != null
          ? data.conversation.id
          : null
        return {
          messages,
          conversationId,
          pagination: data.pagination || null,
          conversation: data.conversation || null,
          ok: true,
        }
      })
      .catch(() => ({messages: [], conversationId: null, pagination: null, conversation: null, ok: false}))
}

function messengerPayload(resp) {
  if (!resp || String(resp.type).toUpperCase() !== 'SUCCESS' || !resp.data || typeof resp.data !== 'object') {
    throw new Error('CHAT_REQUEST_FAILED')
  }
  return resp.data
}

export function sendMessengerText(conversationId, text) {
  if (conversationId == null || conversationId === '') {
    return Promise.reject(new Error('no_conversation'))
  }
  return portalRequest('messenger', 'sendMessage', {conversionId: conversationId}, {
    contents: text,
    type: 'text',
  }).then((resp) => {
    const saved = messengerPayload(resp)
    if (saved.id == null || saved.type !== 'text' || typeof saved.contents !== 'string'
        || String(saved.conversation_id) !== String(conversationId)) {
      throw new Error('CHAT_RESPONSE_INVALID')
    }
    return saved
  })
}

export function isTherapistContact(contact) {
  return !!contact && contact.contact_role === 'therapist'
}

export function pickTherapistContact(contacts, profile) {
  const list = (Array.isArray(contacts) ? contacts : []).filter(isTherapistContact)
  const doctor = resolveDoctor(profile)
  // Profile assignedDoctor.id is the doctors-table id; messenger contacts use the user id.
  const peerUserId = doctor && (doctor.user_id || (doctor.user && doctor.user.id))
  if (peerUserId != null) {
    const hit = list.find((c) => String(c.id) === String(peerUserId) || String(c.user_id) === String(peerUserId))
    if (hit) return hit
  }
  return null
}

export function resolveChatPeer(preferredUserId, contacts, profile) {
  const list = (Array.isArray(contacts) ? contacts : []).filter(isTherapistContact)
  if (preferredUserId != null && preferredUserId !== '') {
    return list.find((c) => String(c.id) === String(preferredUserId)) || null
  }
  return pickTherapistContact(list, profile)
}

export function normalizeChatMessages(messages, profileId, peerName, youLabel) {
  const rows = Array.isArray(messages) ? messages : []
  return rows
      .filter((m) => m && (m.type === 'text' || !m.type || m.contents))
      .map((m, i) => {
        const mine = m.is_mine === true || (profileId != null && String(m.user_id) === String(profileId))
        const rawTime = m.created_at || m.time || ''
        let timeLabel = rawTime || m.date || ''
        const parsed = parseMomentSafe(rawTime)
        if (parsed) timeLabel = parsed.format('HH:mm')
        return {
          id: m.id != null ? String(m.id) : `m-${i}`,
          text: String(m.contents || m.body || m.text || ''),
          mine,
          author: mine ? youLabel : (peerName || ''),
          time: timeLabel,
          raw: m,
        }
      })
      .filter((m) => m.text)
}

export function fetchSupportPolicy() {
  if (!hasAuthToken()) return Promise.resolve(null)
  return portalRequest('care', 'supportPolicy').then(careReceipt)
}

export function fetchSupportRequests(page = 1) {
  if (!hasAuthToken()) return Promise.resolve({data: [], current_page: 1, last_page: 1})
  return portalRequest('care', 'supportRequests', {page}).then((resp) => {
    // Keep the server paginator, including its current/last page and total.
    const payload = careReceipt(resp)
    if (Array.isArray(payload)) {
      return {data: payload, current_page: 1, last_page: 1, total: payload.length}
    }
    if (payload && Array.isArray(payload.data)) return payload
    return {data: [], current_page: 1, last_page: 1}
  })
}

export function createSupportRequest(topic, details, idempotencyKey) {
  return portalRequest('care', 'createSupportRequest', {}, {
    topic: String(topic || '').toUpperCase(),
    details: String(details || '').trim(),
    idempotency_key: idempotencyKey,
  }).then(careReceipt)
}

export function fetchSupportRequest(requestId) {
  if (!hasAuthToken() || !requestId) return Promise.resolve(null)
  return portalRequest('care', 'supportRequest', {supportRequestId: requestId}).then(careReceipt)
}

export function sendSupportRequestMessage(requestId, body) {
  return portalRequest('care', 'sendSupportRequestMessage', {supportRequestId: requestId}, {
    body: String(body || '').trim(),
  }).then(careReceipt)
}

export function supportStatusCode(status) {
  return String(status || 'OPEN').toUpperCase()
}

export function supportStatusTone(status) {
  const raw = supportStatusCode(status)
  if (raw === 'OPEN') return 'warn'
  if (raw === 'RESOLVED') return 'ok'
  if (raw === 'BLOCKED') return 'danger'
  return 'muted'
}

export function supportStatusI18nKey(status) {
  return 'intake.portal.supportStatus_' + supportStatusCode(status).toLowerCase()
}

export function supportTopicI18nKey(topic) {
  return 'intake.portal.requestTopic_' + String(topic || 'booking').toLowerCase()
}

export function formatSupportWhen(iso, lang = 'fa') {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return String(iso)
  return date.toLocaleString(lang === 'fa' ? 'fa-IR' : 'en-GB', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function supportRequestAsListItem(req, lang = 'fa') {
  if (!req || !req.id) return null
  const topic = String(req.topic || '')
  const status = supportStatusCode(req.simple_status || req.status)
  const when = formatSupportWhen(req.last_update_at || req.created_at, lang)
  return {
    id: String(req.id),
    title: topic,
    meta: when,
    status,
    topic,
    source: 'server',
  }
}
